import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from "libphonenumber-js";

/**
 * Normalize phone number to E.164 format
 * E.164 format: +[country code][number] (e.g., +919110686036)
 * 
 * @param {string} phoneNumber - Phone number in any format
 * @param {string} defaultCountry - Default country code (e.g., 'US', 'IN')
 * @returns {string|null} - Normalized phone number in E.164 format or null if invalid
 */
export const normalizeToE164 = (phoneNumber, defaultCountry = "US") => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return null;
  }

  const trimmedPhone = phoneNumber.toString().trim();
  const compactDigits = trimmedPhone.replace(/\D/g, "");
  // +4085551212 is a US number with a plus but no country code.
  if (
    trimmedPhone.startsWith("+") &&
    compactDigits.length === 10 &&
    /^[2-9]\d{2}[2-9]\d{6}$/.test(compactDigits)
  ) {
    return `+1${compactDigits}`;
  }

  try {
    // First, try parsing with default country
    let phoneNumberObj = parsePhoneNumber(trimmedPhone, defaultCountry);
    
    // If that fails and number starts with +, try parsing as international
    if (!phoneNumberObj && trimmedPhone.startsWith("+")) {
      phoneNumberObj = parsePhoneNumber(trimmedPhone);
    }
    
    // If still no object, try parsing without country (might work for some formats)
    if (!phoneNumberObj) {
      phoneNumberObj = parsePhoneNumber(trimmedPhone);
    }
    
    // Check if we got a valid phone number object
    if (phoneNumberObj) {
      // Check if the phone number is valid
      if (phoneNumberObj.isValid()) {
        // Return in E.164 format (e.g., +919110686036)
        return phoneNumberObj.format("E.164");
      }
      
      // Even if not strictly valid, try to format it (might be a valid format)
      try {
        const e164 = phoneNumberObj.format("E.164");
        if (e164 && e164.startsWith("+")) {
          return e164;
        }
      } catch (e) {
        // Ignore formatting errors
      }
    }
    
    // Fallback: if number already starts with + and looks like E.164, return as is.
    // A + plus 10-digit US number is repaired above and must not be returned unchanged.
    if (trimmedPhone.startsWith("+") && /^\+[1-9]\d{9,14}$/.test(trimmedPhone.replace(/\s/g, ""))) {
      const repaired = `+${compactDigits}`;
      if (
        compactDigits.length === 10 &&
        /^[2-9]\d{2}[2-9]\d{6}$/.test(compactDigits)
      ) {
        return `+1${compactDigits}`;
      }
      return repaired.length ? repaired : trimmedPhone.replace(/\s/g, "");
    }
    
    return null;
  } catch (error) {
    // If all parsing fails, try fallback
    if (trimmedPhone.startsWith("+") && /^\+[1-9]\d{9,14}$/.test(trimmedPhone.replace(/\s/g, ""))) {
      if (
        compactDigits.length === 10 &&
        /^[2-9]\d{2}[2-9]\d{6}$/.test(compactDigits)
      ) {
        return `+1${compactDigits}`;
      }
      return trimmedPhone.replace(/\s/g, "");
    }
    console.warn("Error normalizing phone number:", error, phoneNumber);
    return null;
  }
};

/**
 * Validate phone number
 * 
 * @param {string} phoneNumber - Phone number to validate
 * @param {string} defaultCountry - Default country code
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePhoneNumber = (phoneNumber, defaultCountry = "US") => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return false;
  }

  try {
    return isValidPhoneNumber(phoneNumber, defaultCountry);
  } catch (error) {
    return false;
  }
};

/**
 * Mask a phone number for display, keeping only the country code and the
 * last 2 digits visible (rest replaced with X).
 * e.g. "+919113527175" -> "+91XXXXXXXX75"
 *
 * @param {string} phoneNumber - Phone number (E.164 preferred)
 * @returns {string} - Masked phone number
 */
export const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.toString().trim() === "") {
    return "";
  }

  const raw = phoneNumber.toString().trim();

  let countryCode = "";
  let nationalNumber = "";

  try {
    const phoneNumberObj = parsePhoneNumber(raw);
    if (phoneNumberObj) {
      countryCode = `+${phoneNumberObj.countryCallingCode}`;
      nationalNumber = phoneNumberObj.nationalNumber;
    }
  } catch (error) {
    /* fall back to manual parsing below */
  }

  if (!nationalNumber) {
    const digits = raw.replace(/[^\d]/g, "");
    if (!digits) return raw;
    // Best-effort: assume 1-3 digit country code when prefixed with "+".
    if (raw.startsWith("+")) {
      const cc = digits.slice(0, Math.max(1, digits.length - 10));
      countryCode = `+${cc}`;
      nationalNumber = digits.slice(cc.length);
    } else {
      nationalNumber = digits;
    }
  }

  if (nationalNumber.length <= 2) {
    return `${countryCode}${nationalNumber}`;
  }

  const visible = nationalNumber.slice(-2);
  const masked = "X".repeat(nationalNumber.length - 2);
  return `${countryCode}${masked}${visible}`;
};

/**
 * Format phone number for display (international format)
 * 
 * @param {string} phoneNumber - Phone number in E.164 format
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneNumberForDisplay = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return "";
  }

  try {
    const phoneNumberObj = parsePhoneNumber(phoneNumber);
    if (phoneNumberObj && phoneNumberObj.isValid()) {
      return phoneNumberObj.formatInternational();
    }
    return phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
};

